import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { ThemeService } from './core/services/theme.service';
import { EngagementHubService } from './core/services/engagement-hub.service';
import { SudoPromptComponent } from './features/sudo-prompt/sudo-prompt.component';
import { PushPermissionPromptComponent } from './features/push-permission/push-permission-prompt.component';
import { BroadcastToastComponent } from './features/broadcast/broadcast-toast.component';
import { CallOverlayComponent } from './features/video/call-overlay.component';
import { CallService } from './core/services/call.service';

/**
 * Root component for `apps/mobile`.
 *
 * Renders the Ionic shell (`<ion-app>` + `<ion-router-outlet>`) and mounts
 * the global `<mob-sudo-prompt />`, `<mob-push-permission-prompt />`, and
 * `<mob-broadcast-toast />` overlays so they can fly in over any feature
 * page without each feature having to import them. The broadcast toast
 * surfaces admin broadcasts pushed over the engagement hub (the mobile
 * half of the web broadcast feature).
 *
 * The root injects:
 *  - `ThemeService` so its constructor effect runs as soon as the root
 *    component instantiates — the very first paint has the neutral /
 *    cached theme wired into `:root` via CSS variables.
 *  - `EngagementHubService` so the SignalR engagement-hub client
 *    (presence + chat) is created at app startup and the constructor
 *    effect immediately connects to `/hubs/engagement` and invokes
 *    `JoinEventGroup(eventId)` for the active token-bag entry. Without
 *    this, the mobile user would never appear as online in chat and
 *    incoming direct-message events would only land while the inbox /
 *    DM thread surfaces have explicitly opened a hub of their own.
 */
@Component({
  selector: 'mob-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonApp,
    IonRouterOutlet,
    SudoPromptComponent,
    PushPermissionPromptComponent,
    BroadcastToastComponent,
    CallOverlayComponent
  ],
  template: `
    <ion-app>
      <ion-router-outlet />
      <mob-sudo-prompt />
      <mob-push-permission-prompt />
      <mob-broadcast-toast />
      <mob-call-overlay />
    </ion-app>
  `
})
export class App {
  private readonly theme = inject(ThemeService);
  private readonly engagementHub = inject(EngagementHubService);
  private readonly calls = inject(CallService);
  constructor() {
    void this.theme;
    // Created eagerly so the engagement-hub call signalling (incoming-call
    // ring) and LiveKit credential exchange are wired the moment the shell
    // boots — an incoming networking call surfaces over any page, even if
    // the user never opened the People or Booths tab.
    void this.engagementHub;
    void this.calls;
  }
}
